import { InternalGetInternalSessionsExportData } from '@bioliberty/lifemind';
import { getQueryCacheKey } from '../util';
import { downloadExportResponse } from './export-download';
import { tms } from './tms';

interface InternalUtilizationQueryParams {
  dateFrom?: string;
  dateTo?: string;
  timezone: string;
  includeNewPatients: boolean;
}

interface InternalLifeglovUsageQueryParams {
  tenantIds?: string[];
}

interface InternalSurveyResponsesQueryParams {
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface SurveySummary {
  questionSummaries: Record<string, Record<string, SurveyAnswerSummary>>;
}

interface SurveyAnswerSummary {
  questionId: number;
  count: number;
  percentage: number;
}

interface SurveyResponseDetailsQueryParams {
  questionId: number;
  answerValue: number;
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export interface SurveyRespondent {
  patientId: number;
  patientName: string;
  clinicName: string;
}

export interface SurveyResponseDetails {
  questionId: number;
  answerValue: number;
  count: number;
  percentage: number;
  respondents: SurveyRespondent[];
}

// Grouped in SQL by patient-services: one row per clinic and month (1-12, in the viewer's timezone)
export interface UtilizationMonth {
  year: number;
  month: number;
}

export interface SessionUtilization extends UtilizationMonth {
  sessionCount: number;
  distinctPatients: number;
  durationMs: number;
  totalTimeTaken: number;
  totalReps: number;
  activityCount: number;
}

export interface ClinicSessionUtilization extends SessionUtilization {
  subtenantName: string;
}

export interface NewPatientsUtilization extends UtilizationMonth {
  count: number;
}

export interface ClinicNewPatientsUtilization extends NewPatientsUtilization {
  subtenantName: string;
}

export interface InternalUtilization {
  clinicSessions: ClinicSessionUtilization[];
  sessionTotals: SessionUtilization[];
  clinicNewPatients: ClinicNewPatientsUtilization[];
  newPatientTotals: NewPatientsUtilization[];
}

export interface InternalLifeglovUsage {
  totalLifeglovs: number;
  leftHandSmallSizeCount: number;
  leftHandMediumSizeCount: number;
  leftHandLargeSizeCount: number;
  rightHandSmallSizeCount: number;
  rightHandMediumSizeCount: number;
  rightHandLargeSizeCount: number;
}

// Seconds a tab's data stays cached after leaving it, so switching back shows it without refetching
const KEEP_UNUSED_DATA_FOR = 300;

const api = tms.injectEndpoints({
  endpoints: (builder) => ({
    getInternalUtilization: builder.query<InternalUtilization, InternalUtilizationQueryParams>({
      query: (params) => ({
        url: `internal/utilization`,
        method: 'GET',
        params
      }),
      providesTags: (_result, _error, params) => [{ type: 'Internal', id: getQueryCacheKey(params, 'utilization') }],
      keepUnusedDataFor: KEEP_UNUSED_DATA_FOR
    }),
    getInternalLifeglovUsage: builder.query<Record<string, InternalLifeglovUsage>, InternalLifeglovUsageQueryParams>({
      query: (params) => ({
        url: `internal/lifeglov-usage`,
        method: 'GET',
        params: {
          subtenantNames: params.tenantIds?.join(',')
        }
      }),
      providesTags: (_result, _error, params) => [{ type: 'Internal', id: getQueryCacheKey(params, 'lifeglov-usage') }],
      keepUnusedDataFor: KEEP_UNUSED_DATA_FOR
    }),
    getInternalSessionsExport: builder.mutation<void, InternalGetInternalSessionsExportData['query']>({
      query: (params) => ({
        url: `internal/export-sessions`,
        method: 'GET',
        params,
        responseHandler: downloadExportResponse,
        cache: 'no-store'
      })
    }),
    getSurveyResponses: builder.query<SurveySummary, InternalSurveyResponsesQueryParams>({
      query: (params) => ({
        url: `internal/survey-responses`,
        method: 'GET',
        params: {
          subtenantIds: params.tenantIds?.join(','),
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      }),
      providesTags: (_result, _error, params) => [
        { type: 'Internal', id: getQueryCacheKey(params, 'survey-response') }
      ],
      keepUnusedDataFor: KEEP_UNUSED_DATA_FOR
    }),
    getSurveyResponseDetails: builder.query<SurveyResponseDetails, SurveyResponseDetailsQueryParams>({
      query: (params) => ({
        url: `internal/survey-response-details`,
        method: 'GET',
        params: {
          questionId: params.questionId,
          answerValue: params.answerValue,
          subtenantIds: params.tenantIds?.join(','),
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      }),
      providesTags: (_result, _error, params) => [
        { type: 'Internal', id: getQueryCacheKey(params, 'survey-response-details') }
      ],
      keepUnusedDataFor: KEEP_UNUSED_DATA_FOR
    })
  }),
  overrideExisting: false
});

export const {
  useGetInternalUtilizationQuery,
  useGetInternalLifeglovUsageQuery,
  useGetInternalSessionsExportMutation,
  useGetSurveyResponsesQuery,
  useGetSurveyResponseDetailsQuery
} = api;
